import { useState, useEffect, useMemo, useRef } from "react";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Filler, Tooltip, Legend } from "chart.js";
import { Line } from "react-chartjs-2";
import { useApp } from "../context/AppContext";
import CocoonMini from "../components/CocoonMini";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Filler, Tooltip, Legend);

// ─── Color tokens (Spalah-themed) ─────────────────────────────────
const C = {
  brand: "#6366f1",
  brand2: "#a855f7",
  pos: "#10b981",
  neg: "#ef4444",
  warn: "#f59e0b",
  info: "#3b82f6",
};

// ─── Inline SVG components ────────────────────────────────────────

function Donut({ value, color = C.brand, size = 88, stroke = 9, label }) {
  const r = (size - stroke) / 2;
  const C2 = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value || 0));
  const off = C2 * (1 - pct / 100);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: "block" }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(127,127,127,0.15)" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={C2} strokeDashoffset={off} strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: "stroke-dashoffset 0.6s ease" }} />
      <text x="50%" y="50%" textAnchor="middle" dy="0.35em" fontSize={size * 0.28} fontWeight="700" fill={color} fontFamily="-apple-system, sans-serif">{Math.round(pct)}{label === false ? "" : "%"}</text>
    </svg>
  );
}

function Gauge({ value, label, size = 64 }) {
  const v = Math.max(0, Math.min(100, value || 0));
  const color = v >= 90 ? C.pos : v >= 50 ? C.warn : C.neg;
  return (
    <div style={{ textAlign: "center" }}>
      <Donut value={v} color={color} size={size} stroke={6} />
      <div style={{ fontSize: 9, fontWeight: 700, color: color, marginTop: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
    </div>
  );
}

function Radar({ axes, values, size = 240, t, dark }) {
  const cx = size / 2, cy = size / 2, R = size / 2 - 28;
  const N = axes.length;
  const grids = [0.25, 0.5, 0.75, 1];
  const ptOn = (angle, dist) => [cx + Math.cos(angle) * dist, cy + Math.sin(angle) * dist];
  const angles = axes.map((_, i) => -Math.PI / 2 + (2 * Math.PI * i) / N);
  const dataPath = angles.map((a, i) => {
    const r = (Math.max(0, Math.min(100, values[i] || 0)) / 100) * R;
    const [x, y] = ptOn(a, r);
    return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ") + " Z";
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: "block" }}>
      {grids.map((g, gi) => (
        <polygon key={gi}
          points={angles.map(a => ptOn(a, R * g).map(v => v.toFixed(1)).join(",")).join(" ")}
          fill="none" stroke={dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"} strokeWidth="1" />
      ))}
      {angles.map((a, i) => {
        const [x, y] = ptOn(a, R);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke={dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"} strokeWidth="1" />;
      })}
      <path d={dataPath} fill={C.brand} fillOpacity="0.18" stroke={C.brand} strokeWidth="2" />
      {angles.map((a, i) => {
        const r = (Math.max(0, Math.min(100, values[i] || 0)) / 100) * R;
        const [x, y] = ptOn(a, r);
        return <circle key={i} cx={x} cy={y} r="3" fill={C.brand} />;
      })}
      {axes.map((label, i) => {
        const [x, y] = ptOn(angles[i], R + 16);
        return <text key={i} x={x} y={y} textAnchor="middle" dy="0.35em" fontSize="10" fill={t.textMuted} fontFamily="-apple-system, sans-serif">{label}</text>;
      })}
    </svg>
  );
}

function MiniSparkline({ data, color = C.brand, h = 28, w = 80 }) {
  if (!data || data.length < 2) return <div style={{ height: h, width: w }} />;
  const max = Math.max(...data), min = Math.min(...data), span = Math.max(1, max - min);
  const step = w / (data.length - 1);
  const pts = data.map((v, i) => `${(i * step).toFixed(1)},${(h - ((v - min) / span) * h).toFixed(1)}`).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: "block" }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={(w).toFixed(1)} cy={(h - ((data[data.length - 1] - min) / span) * h).toFixed(1)} r="2" fill={color} />
    </svg>
  );
}

function KPI({ label, value, delta, deltaKind, deltaUnit = "%", spark, color = C.brand, t, dark }) {
  const dKind = deltaKind || (delta == null ? "neutral" : delta > 0 ? "pos" : delta < 0 ? "neg" : "neutral");
  const dColor = dKind === "pos" ? C.pos : dKind === "neg" ? C.neg : dKind === "warn" ? C.warn : t.textMuted;
  return (
    <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 12, padding: 14, position: "relative" }}>
      <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 6 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: t.text, lineHeight: 1.1 }}>{value == null ? "—" : (typeof value === "number" ? value.toLocaleString("uk-UA") : value)}</div>
          {delta != null && (
            <div style={{ fontSize: 11, color: dColor, fontWeight: 600, marginTop: 2 }}>
              {delta > 0 ? "+" : ""}{delta}{deltaUnit}
            </div>
          )}
        </div>
        {spark && spark.length > 1 && <MiniSparkline data={spark} color={color} h={28} w={70} />}
      </div>
    </div>
  );
}

function Heatmap({ rows, cols, data, t, dark }) {
  if (!rows?.length || !cols?.length) return <div style={{ color: t.textMuted, fontSize: 12, padding: 20, textAlign: "center" }}>Немає даних для теплової карти</div>;
  const cell = 22;
  const labelW = 220;
  const dateRowH = 18;
  const w = labelW + cols.length * cell + 30;
  const h = rows.length * cell + dateRowH + 4;
  const colorFor = (pos) => {
    if (pos == null) return dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)";
    if (pos <= 3) return C.pos;
    if (pos <= 10) return "#84cc16";
    if (pos <= 30) return C.warn;
    if (pos <= 50) return "#fb923c";
    return C.neg;
  };
  const opacityFor = (pos) => {
    if (pos == null) return 1;
    if (pos <= 3) return 1;
    if (pos <= 10) return 0.85;
    if (pos <= 30) return 0.7;
    if (pos <= 50) return 0.55;
    return 0.4;
  };
  // X-axis labels — show every Nth date so labels don't overlap (target 6-8 visible)
  const tickEvery = Math.max(1, Math.ceil(cols.length / 8));
  return (
    <div style={{ overflowX: "auto" }}>
      <svg width={w} height={h} style={{ display: "block" }}>
        {/* X-axis date labels */}
        {cols.map((d, ci) => {
          if (ci % tickEvery !== 0 && ci !== cols.length - 1) return null;
          return (
            <text key={`d-${ci}`} x={labelW + ci * cell + cell / 2} y={dateRowH - 4}
              textAnchor="middle" fontSize="9.5" fill={t.textMuted} fontFamily="-apple-system, sans-serif">
              {fmtDateShort(d)}
            </text>
          );
        })}
        {/* Rows */}
        {rows.map((r, ri) => (
          <g key={ri} transform={`translate(0, ${dateRowH})`}>
            <text x={labelW - 8} y={ri * cell + cell / 2 + 4} textAnchor="end" fontSize="11" fill={t.text} fontFamily="-apple-system, sans-serif" fontWeight="500">
              {r.length > 28 ? r.slice(0, 27) + "…" : r}
            </text>
            {cols.map((d, ci) => {
              const v = data[ri]?.[ci];
              return (
                <rect key={ci} x={labelW + ci * cell} y={ri * cell} width={cell - 2} height={cell - 2} rx="3"
                  fill={colorFor(v)} fillOpacity={opacityFor(v)}>
                  <title>{`${r}\nДата: ${fmtDate(d)}\nПозиція: ${v == null ? "немає даних" : Number(v).toFixed(1)}`}</title>
                </rect>
              );
            })}
          </g>
        ))}
      </svg>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────

function sumLast(arr, n, key) {
  if (!Array.isArray(arr)) return 0;
  return arr.slice(-n).reduce((s, x) => s + (x?.[key] || 0), 0);
}

function deltaPct(curr, prev) {
  if (!prev || prev === 0) return curr > 0 ? 100 : null;
  return Math.round(((curr - prev) / prev) * 100);
}

// Format dates as DD.MM.YYYY (Ukrainian convention)
function fmtDate(d) {
  if (!d) return "";
  const s = String(d).slice(0, 10); // "2026-04-10"
  const parts = s.split("-");
  if (parts.length !== 3) return s;
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
}
function fmtDateShort(d) {
  if (!d) return "";
  const s = String(d).slice(0, 10);
  const parts = s.split("-");
  if (parts.length !== 3) return s;
  return `${parts[2]}.${parts[1]}`;
}

// ─── Main view ────────────────────────────────────────────────────

// ─── Індексація в Google (URL Inspection через власний GSC проекту) ───
function IndexCoverageWidget({ t, dark, rows, sum, onRefresh, refreshing, refreshLabel, setView }) {
  const card = { background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 12, padding: 18 };
  const total = sum?.total || (rows ? rows.length : 0);
  const indexed = sum?.indexed != null ? sum.indexed : (rows ? rows.filter(r => r.verdict === "PASS").length : 0);
  const notIndexed = sum?.notIndexed != null ? sum.notIndexed : Math.max(0, total - indexed);
  const pct = total > 0 ? Math.round(indexed / total * 100) : 0;
  const notList = (rows || []).filter(r => r.verdict && r.verdict !== "PASS").slice(0, 8);
  const lastChecked = (rows || []).map(r => r.checked_at).filter(Boolean).sort().slice(-1)[0];
  const header = (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
      <div>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.brand, textTransform: "uppercase", letterSpacing: 0.6 }}>ІНДЕКСАЦІЯ · GOOGLE</div>
        <h3 style={{ margin: "4px 0 0", fontSize: 14, fontWeight: 600, color: t.text }}>🔎 Чи бачить вас Google</h3>
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {lastChecked && <span style={{ fontSize: 10, color: t.textMuted }}>перевірено {new Date(lastChecked).toLocaleDateString("uk-UA")}</span>}
        <button onClick={onRefresh} disabled={refreshing} style={{ background: t.bgCard, border: `1px solid ${C.warn}`, color: C.warn, borderRadius: 8, padding: "5px 12px", fontSize: 11, fontWeight: 600, cursor: "pointer", opacity: refreshing ? 0.6 : 1, fontFamily: "inherit" }}>{refreshing ? (refreshLabel || "Перевіряю…") : "↻ Перевірити"}</button>
      </div>
    </div>
  );
  if (!total) {
    return <div style={card}>{header}<div style={{ fontSize: 12.5, color: t.textMuted, lineHeight: 1.6 }}>Перевірка індексації ще не запускалась. Натисніть «↻ Перевірити» — Spalah зробить запит Google <code>site:</code> по сторінках проекту (через Serper, без квоти GSC) і покаже, що реально в індексі Google, а що ні.</div></div>;
  }
  return (
    <div style={card}>
      {header}
      <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 20, alignItems: "center" }}>
        <Donut value={pct} color={pct >= 80 ? C.pos : pct >= 50 ? C.warn : C.neg} size={96} />
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", gap: 18, marginBottom: 12, flexWrap: "wrap" }}>
            <div><div style={{ fontSize: 22, fontWeight: 800, color: C.pos }}>{indexed}</div><div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, textTransform: "uppercase" }}>У індексі</div></div>
            <div><div style={{ fontSize: 22, fontWeight: 800, color: notIndexed > 0 ? C.neg : t.text }}>{notIndexed}</div><div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, textTransform: "uppercase" }}>Не в індексі</div></div>
            <div><div style={{ fontSize: 22, fontWeight: 800, color: t.text }}>{total}</div><div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, textTransform: "uppercase" }}>Перевірено</div></div>
          </div>
          {notList.length > 0 ? (
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, marginBottom: 6 }}>Не в індексі:</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 170, overflow: "auto" }}>
                {notList.map((r, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 11, minWidth: 0 }}>
                    <span style={{ width: 6, height: 6, borderRadius: 3, background: C.neg, flexShrink: 0 }} />
                    <a href={r.url} target="_blank" rel="noreferrer" style={{ color: C.brand, textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>{(r.url || "").replace(/^https?:\/\/[^/]+/, "") || "/"}</a>
                    <span style={{ color: t.textMuted, fontSize: 10, flexShrink: 0 }}>{r.coverage_state || r.verdict}</span>
                  </div>
                ))}
              </div>
              {notIndexed > notList.length && setView && <button onClick={() => setView("gsc_analysis")} style={{ marginTop: 8, background: "transparent", border: "none", color: C.brand, fontSize: 11, cursor: "pointer", padding: 0, fontFamily: "inherit" }}>усі {notIndexed} → у модулі «Аналіз GSC»</button>}
            </div>
          ) : (
            <div style={{ fontSize: 12.5, color: C.pos, fontWeight: 600 }}>✓ Усі перевірені сторінки в індексі Google.</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DashboardView() {
  const { proj, activeId, apiFetch, dark, t, flash, setView } = useApp();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [psStrategy, setPsStrategy] = useState("mobile");
  const [covLoading, setCovLoading] = useState(false);
  const [covReloadKey, setCovReloadKey] = useState(0);
  const [covLabel, setCovLabel] = useState("");
  const [ipChk, setIpChk] = useState(null);     // {loading,result} перевірки бану IP Spalah
  const [ipModal, setIpModal] = useState(false); // попап з інструкцією для розробника
  const runIpCheck = async () => {
    setIpChk({ loading: true });
    try {
      const r = await apiFetch(`/api/projects/${activeId}/ip-check`);
      const d = await r.json();
      setIpChk({ loading: false, result: d });
      if (d.blocked) setIpModal(true);
    } catch (e) { setIpChk({ loading: false, result: { error: e.message } }); }
  };
  const [idxData, setIdxData] = useState(null); // {rows, summary} з перевірки Google site: (Serper)
  useEffect(() => {
    if (!activeId) return;
    apiFetch(`/api/projects/${activeId}/pages-overview/index-data`).then(r => r.json()).then(d => setIdxData(d || null)).catch(() => setIdxData(null));
  }, [activeId, covReloadKey]); // eslint-disable-line

  const load = (force) => {
    setLoading(!data);
    if (force) setRefreshing(true);
    apiFetch(`/api/projects/${activeId}/dashboard${force ? "?refresh=1" : ""}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); setRefreshing(false); })
      .catch(() => { setLoading(false); setRefreshing(false); flash && flash("Помилка завантаження дашборду"); });
  };

  useEffect(() => { if (activeId) load(false); }, [activeId]);

  // Перевірка індексації через Google `site:` (Serper, без GSC-квоти). Фонова задача + polling.
  const checkCoverage = async () => {
    if (covLoading) return;
    setCovLoading(true);
    setCovLabel("Запуск…");
    try {
      const r = await apiFetch(`/api/projects/${activeId}/pages-overview/index-check`, { method: "POST" });
      const d = await r.json();
      if (!r.ok) { flash && flash("Індексація: " + (d.error || "помилка")); setCovLoading(false); setCovLabel(""); return; }
      setCovLabel(`0/${d.total}`);
      const poll = setInterval(async () => {
        try {
          const sR = await apiFetch(`/api/tasks/${d.taskId}`);
          if (!sR.ok) return;
          const s = await sR.json();
          setCovLabel(`${s.progress?.current || 0}/${s.progress?.total || d.total}`);
          if (s.status === "completed" || s.status === "failed") {
            clearInterval(poll);
            setCovLoading(false);
            setCovLabel("");
            if (s.status === "completed") { setCovReloadKey(k => k + 1); flash && flash(`✓ Індексацію перевірено (в індексі: ${s.progress?.indexed ?? "?"})`); }
            else flash && flash("Індексація: помилка перевірки");
          }
        } catch { /* keep polling */ }
      }, 2000);
    } catch (e) { flash && flash("Індексація: " + (e.message || "помилка")); setCovLoading(false); setCovLabel(""); }
  };

  if (!proj) return null;
  if (loading && !data) {
    return <div style={{ textAlign: "center", padding: 60, color: t.textMuted }}>Завантаження дашборду...</div>;
  }
  if (!data) {
    return <div style={{ textAlign: "center", padding: 60, color: t.textMuted }}>Немає даних</div>;
  }

  // ─── Derive metrics ─────────────────────────────────────────────
  // /api/gsc/daily returns { rows: [...] }
  const dailyArr = Array.isArray(data.gscDaily?.rows) ? data.gscDaily.rows
    : Array.isArray(data.gscDaily?.dailyData) ? data.gscDaily.dailyData
    : Array.isArray(data.gscDaily?.daily) ? data.gscDaily.daily
    : Array.isArray(data.gscDaily) ? data.gscDaily
    : [];
  const last30Clicks = sumLast(dailyArr, 30, "clicks");
  const prev30Clicks = sumLast(dailyArr.slice(0, -30), 30, "clicks");
  const last30Imp = sumLast(dailyArr, 30, "impressions");
  const prev30Imp = sumLast(dailyArr.slice(0, -30), 30, "impressions");

  // Position snapshots: prefer Serper-based position_snapshots if any, else fall back to GSC-based
  const positionSnaps = Array.isArray(data.positionSnaps) ? data.positionSnaps : [];
  const gscSnaps = Array.isArray(data.snapshots?.snapshots) ? data.snapshots.snapshots : [];
  const snapsArr = positionSnaps.length ? positionSnaps : gscSnaps;
  const snapsSource = positionSnaps.length ? "Serper · перевірка позицій" : "Google Search Console";
  const latest = snapsArr[0];
  const oldest = snapsArr[snapsArr.length - 1];
  const top3Now = latest?.top3 || 0, top3Old = oldest?.top3 || 0;
  const top10Now = latest?.top10 || 0, top10Old = oldest?.top10 || 0;
  const top100Now = latest?.top100 || 0, top100Old = oldest?.top100 || 0;
  const totalQ = latest?.totalQueries || 0, totalQOld = oldest?.totalQueries || 0;

  // Build sparklines from snapshots (oldest → newest = 12 most recent)
  const recentSnaps = [...snapsArr].reverse().slice(-12);
  const sparkTop3 = recentSnaps.map(s => s.top3 || 0);
  const sparkTop10 = recentSnaps.map(s => s.top10 || 0);
  const sparkTop100 = recentSnaps.map(s => s.top100 || 0);
  const sparkTotal = recentSnaps.map(s => s.totalQueries || 0);
  const sparkClicks = dailyArr.slice(-30).map(d => d.clicks || 0);
  const sparkImp = dailyArr.slice(-30).map(d => d.impressions || 0);

  // AI visibility per source
  const aiResults = Array.isArray(data.aiVis?.results) ? data.aiVis.results : [];
  const aiTotal = aiResults.length;
  const aiCited = aiResults.filter(r => r.cited).length;
  const aiPct = aiTotal ? Math.round((aiCited / aiTotal) * 100) : 0;
  const bySource = {};
  for (const r of aiResults) {
    const src = (r.source || "other").toLowerCase();
    if (!bySource[src]) bySource[src] = { total: 0, cited: 0 };
    bySource[src].total++;
    if (r.cited) bySource[src].cited++;
  }
  const SOURCE_META = {
    chatgpt: { label: "ChatGPT", color: C.pos },
    claude: { label: "Claude", color: C.brand2 },
    perplexity: { label: "Perplexity", color: C.info },
    gemini: { label: "Gemini", color: C.warn },
    other: { label: "Інші", color: t.textMuted },
  };
  const aiSourcesList = Object.entries(bySource).map(([k, v]) => ({
    key: k,
    label: SOURCE_META[k]?.label || k,
    color: SOURCE_META[k]?.color || C.brand,
    pct: v.total ? Math.round((v.cited / v.total) * 100) : 0,
  })).sort((a, b) => b.pct - a.pct);

  // Backlink profile — Ahrefs (serpstat_ref_domain_snapshots, monthly cron).
  // Values = latest snapshot; deltas = month-over-month absolute change, computed in the
  // backend (dashboard.js) exactly like Ahrefs «Зміни: Минулий місяць».
  const serpHistory = Array.isArray(data.serpstat?.history) ? data.serpstat.history : [];
  const spOverview = data.serpstat?.overview || {};
  const refLatest = spOverview.ref_domains
    ?? serpHistory[serpHistory.length - 1]?.total_ref_domains
    ?? 0;
  const drLatest = spOverview.dr
    ?? serpHistory[serpHistory.length - 1]?.domain_rating
    ?? 0;
  const blLatest = Number(spOverview.backlinks_live
    ?? serpHistory[serpHistory.length - 1]?.backlinks_live
    ?? 0);
  // Month-over-month absolute deltas (null when <1 month of history)
  const refDelta = spOverview.ref_domains_delta ?? null;
  const drDelta = spOverview.dr_delta ?? null;
  const blDelta = spOverview.backlinks_delta ?? null;

  // Coverage / pages
  const pagesCount = data.linkGraphSummary?.pages || 0;
  // Індексація — з перевірки Google site: (Serper, без GSC-квоти).
  const idxSummary = idxData?.summary || null;
  const indexedPct = idxSummary && idxSummary.total > 0
    ? Math.round((idxSummary.indexed / idxSummary.total) * 100) : null;
  const indexedCount = idxSummary ? idxSummary.indexed : null;
  const notIndexedCount = idxSummary ? idxSummary.notIndexed : null;

  // PageSpeed
  const ps = data.pagespeed?.[psStrategy] || data.pagespeed?.mobile || data.pagespeed?.desktop || null;

  // Health Radar — composite scores from real metrics, fallback 50 when no data
  const radarValues = [
    /* Технічно */    ps ? Math.round(((ps.best_practices || 0) + (ps.seo || 0)) / 2) : 50,
    /* Контент */     indexedPct != null ? indexedPct : 50,
    /* AI */          aiTotal ? aiPct : 50,
    /* Швидкість */   ps ? (ps.performance || 0) : 50,
    /* Беклінки */    refLatest > 0 ? Math.min(100, Math.round(Math.log10(refLatest + 1) * 30)) : 30,
    /* GSC */         dailyArr.length ? Math.min(100, 60 + Math.round((deltaPct(last30Clicks, prev30Clicks) || 0) / 4)) : 30,
    /* Структура */   data.linkGraphSummary?.avgPerPage ? Math.min(100, Math.round(data.linkGraphSummary.avgPerPage * 12)) : 40,
  ];

  // Position distribution from latest snapshot (uses gsc_snapshot_rows aggregation if needed; here we approximate from snap counts)
  const positionDistribution = latest ? [
    { p: "TOP 1-3", v: Math.max(0, top3Now), c: C.brand2 },
    { p: "TOP 4-10", v: Math.max(0, top10Now - top3Now), c: C.info },
    { p: "TOP 11-30", v: Math.max(0, (latest.top30 || top10Now) - top10Now), c: C.warn },
    { p: ">30", v: Math.max(0, top100Now - (latest.top30 || top10Now)), c: t.textMuted },
  ] : [];
  const posDistMax = Math.max(1, ...positionDistribution.map(r => r.v));

  // Tasks (kanban — flatten and pick top non-done; sort by recency)
  const allTasks = (data.kanban || []).flatMap(col => (col.tasks || []).map(tk => ({ ...tk, columnTitle: col.title, columnColor: col.color })));
  const activeTasks = allTasks
    .filter(tk => tk.status !== "done" && tk.status !== "completed")
    .sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))
    .slice(0, 6);
  // Per-column counts shown as a strip
  const kanbanColCounts = (data.kanban || []).map(c => ({
    title: c.title, color: c.color,
    count: (c.tasks || []).filter(tk => tk.status !== "done" && tk.status !== "completed").length,
  })).filter(c => c.count > 0);

  // Heatmap
  const hm = data.heatmap;

  // Chart.js shared options
  const chartOpts = (yLabels = true) => ({
    responsive: true, maintainAspectRatio: false,
    interaction: { intersect: false, mode: "index" },
    plugins: {
      legend: { display: false },
      tooltip: { backgroundColor: dark ? "#1e1e2e" : "#fff", titleColor: t.text, bodyColor: t.text, borderColor: t.border, borderWidth: 1, padding: 10, cornerRadius: 8, bodyFont: { size: 11 } },
    },
    scales: {
      x: { ticks: { color: t.textMuted, font: { size: 10 }, maxTicksLimit: 10 }, grid: { display: false }, border: { display: false } },
      y: { ticks: { display: yLabels, color: t.textMuted, font: { size: 10 } }, grid: { color: dark ? "rgba(255,255,255,.04)" : "rgba(0,0,0,.04)" }, border: { display: false }, beginAtZero: true },
    },
  });

  const card = { background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 12, padding: 18 };
  const labelUp = { fontSize: 10, fontWeight: 700, color: C.brand2, textTransform: "uppercase", letterSpacing: 0.6 };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingBottom: 60 }}>

      {/* ─── IP-ДОСТУП SPALAH ─── */}
      {(() => {
        const rr = ipChk?.result;
        const blocked = rr && rr.blocked;
        const okRes = rr && rr.ok;
        const bg = blocked ? "rgba(239,68,68,0.10)" : okRes ? "rgba(22,163,74,0.10)" : (dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)");
        const bd = blocked ? "rgba(239,68,68,0.35)" : okRes ? "rgba(22,163,74,0.30)" : t.border;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: 10, background: bg, border: `1px solid ${bd}`, fontSize: 12.5 }}>
            <span style={{ fontSize: 16 }}>{blocked ? "🚫" : okRes ? "✅" : "🛡"}</span>
            <div style={{ flex: 1, color: t.text }}>
              {ipChk?.loading ? <strong>Перевіряю доступ із IP Spalah…</strong>
                : blocked ? <><strong>Сайт блокує IP Spalah.</strong> {rr.reason} — потрібен whitelist. <button onClick={() => setIpModal(true)} style={{ background: "none", border: "none", color: C.brand, cursor: "pointer", padding: 0, fontFamily: "inherit", fontSize: 12.5, textDecoration: "underline" }}>що передати розробнику →</button></>
                : okRes ? <><strong>Сайт доступний із зовнішнього IP Spalah</strong> ({rr.ip}{rr.ipv6 ? " / " + rr.ipv6 : ""}) — тексти/позиції/аудит працюватимуть.</>
                : <><strong>Доступ сайту з IP Spalah не перевірено.</strong> Натисніть, щоб переконатися, що сайт не блокує наш сервер.</>}
            </div>
            <button onClick={runIpCheck} disabled={ipChk?.loading}
              style={{ background: t.bgCard, border: `1px solid ${C.brand}`, color: C.brand, borderRadius: 8, padding: "6px 12px", fontSize: 11, fontWeight: 700, cursor: ipChk?.loading ? "wait" : "pointer", opacity: ipChk?.loading ? 0.6 : 1, fontFamily: "inherit", whiteSpace: "nowrap" }}>
              {ipChk?.loading ? "..." : "🔌 Перевірити IP"}
            </button>
          </div>
        );
      })()}

      {ipModal && ipChk?.result && (
        <div onClick={() => setIpModal(false)} style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: dark ? "#1a1a2e" : "#fff", border: `1px solid ${t.border}`, borderRadius: 14, maxWidth: 540, padding: 24, boxShadow: "0 20px 80px rgba(0,0,0,0.5)" }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: t.text, marginBottom: 10 }}>🚫 Сайт блокує IP Spalah</div>
            <div style={{ fontSize: 13, color: t.text, lineHeight: 1.6, marginBottom: 14 }}>{ipChk.result.message}</div>
            <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Передайте розробнику / хостингу:</div>
            <div style={{ background: dark ? "#0f0f1a" : "#f3f4f6", border: `1px solid ${t.border}`, borderRadius: 8, padding: "10px 12px", fontFamily: "monospace", fontSize: 13, color: t.text, marginBottom: 6, userSelect: "all" }}>
              {ipChk.result.cdn
                ? <>Сайт за <strong>{ipChk.result.cdn}</strong>. Додати IP <strong>{ipChk.result.ipv6 ? ipChk.result.ipv4 + " та " + ipChk.result.ipv6 : ipChk.result.ip}</strong> у білий список саме в <strong>{ipChk.result.cdn}</strong> (IP Access Rules / WAF Allow / виняток Bot Fight). На рівні origin-сервера whitelist НЕ спрацює — origin бачить IP {ipChk.result.cdn}, а не наш.</>
                : <>Додати IP <strong>{ipChk.result.ipv6 ? ipChk.result.ipv4 + " та " + ipChk.result.ipv6 : ipChk.result.ip}</strong> у білий список (whitelist / allowlist) на сервері та у Cloudflare/WAF.</>}
            </div>
            <div style={{ fontSize: 11.5, color: t.textMuted, lineHeight: 1.5, marginBottom: 6 }}>ℹ️ {ipChk.result.publicNote || `IP ${ipChk.result.ipv4 || ipChk.result.ip} — публічна зовнішня адреса (не приватна/локальна). Перевірка: whois ${ipChk.result.ipv4 || ipChk.result.ip}.`}</div>
            <div style={{ fontSize: 11.5, color: t.textMuted, marginBottom: 16 }}>Статус відповіді: {ipChk.result.reason || ("HTTP " + ipChk.result.status)} · домен {ipChk.result.domain}{ipChk.result.cdn ? ` · CDN: ${ipChk.result.cdn}` : ""}</div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => { try { navigator.clipboard.writeText(ipChk.result.message); flash && flash("Скопійовано"); } catch {} }} style={{ background: C.brand, color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>📋 Копіювати текст</button>
              <button onClick={() => setIpModal(false)} style={{ background: t.bgCard, color: t.text, border: `1px solid ${t.border}`, borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Закрити</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── HEADER ─── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ ...labelUp, marginBottom: 6 }}>Дашборд проекту</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: t.text, display: "flex", alignItems: "center", gap: 10 }}>
            {proj.name}
            <span style={{ fontSize: 9, fontWeight: 700, padding: "3px 8px", borderRadius: 6, background: "rgba(16,185,129,0.12)", color: C.pos }}>● ACTIVE</span>
          </h1>
          <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4 }}>
            {proj.domain} {pagesCount ? `· ${pagesCount} стор.` : ""} {data._cached ? `· з кешу (${data._age}с)` : "· щойно оновлено"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={checkCoverage} disabled={covLoading}
            style={{ background: t.bgCard, border: `1px solid ${C.warn}`, color: C.warn, borderRadius: 8, padding: "6px 12px", fontSize: 11, fontWeight: 600, cursor: "pointer", opacity: covLoading ? 0.6 : 1, fontFamily: "inherit" }}>
            {covLoading ? (covLabel || "Перевіряю…") : "🔎 Перевірити індекс"}
          </button>
          <button onClick={() => load(true)} disabled={refreshing}
            style={{ background: t.bgCard, border: `1px solid ${t.border}`, color: t.text, borderRadius: 8, padding: "6px 12px", fontSize: 11, fontWeight: 600, cursor: "pointer", opacity: refreshing ? 0.6 : 1, fontFamily: "inherit" }}>
            {refreshing ? "Оновлюю..." : "↻ Оновити"}
          </button>
        </div>
      </div>

      {/* ─── HERO METRICS ─── */}
      <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr 1fr 1fr", gap: 14 }}>
        {/* AI visibility big card — clickable, navigates to module */}
        <div
          onClick={() => setView && setView("ai_visibility")}
          title="Відкрити модуль AI Видимість"
          style={{ ...card, position: "relative", overflow: "hidden", borderColor: "rgba(99,102,241,0.3)", cursor: "pointer", transition: "transform .15s, border-color .15s" }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = C.brand; e.currentTarget.style.transform = "translateY(-1px)"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(99,102,241,0.3)"; e.currentTarget.style.transform = "translateY(0)"; }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={labelUp}>AI ВИДИМІСТЬ · LIVE</div>
            <span style={{ fontSize: 14, color: C.brand, opacity: 0.7 }}>→</span>
          </div>
          <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
            <Donut value={aiPct} color={C.brand} size={84} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 6 }}>
                <span style={{ color: C.pos, fontWeight: 700, fontSize: 13 }}>{aiCited}/{aiTotal}</span> AI-запитів
              </div>
              {aiSourcesList.length === 0 && <div style={{ fontSize: 11, color: t.textMuted }}>Немає даних AI-видимості. Запустіть перевірку в модулі AI Visibility.</div>}
              {aiSourcesList.map(s => (
                <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, marginBottom: 3 }}>
                  <span style={{ color: t.textMuted, width: 60, flexShrink: 0 }}>{s.label}</span>
                  <div style={{ flex: 1, height: 4, borderRadius: 2, background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)", overflow: "hidden", minWidth: 0 }}>
                    <div style={{ height: "100%", width: `${s.pct}%`, background: s.color, transition: "width 0.6s ease" }} />
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 700, color: s.color, width: 32, textAlign: "right", flexShrink: 0 }}>{s.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <KPI label="Кліки (30д)" value={last30Clicks} delta={deltaPct(last30Clicks, prev30Clicks)} spark={sparkClicks} color={C.info} t={t} dark={dark} />
        <KPI label="Покази (30д)" value={last30Imp} delta={deltaPct(last30Imp, prev30Imp)} spark={sparkImp} color={C.brand} t={t} dark={dark} />
        <KPI label="ТОП-10 запитів" value={top10Now} delta={deltaPct(top10Now, top10Old)} spark={sparkTop10} color={C.pos} t={t} dark={dark} />
      </div>

      {/* ─── SECONDARY KPIs ─── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 12 }}>
        <KPI label="ТОП-3" value={top3Now} delta={deltaPct(top3Now, top3Old)} spark={sparkTop3} color={C.pos} t={t} dark={dark} />
        <KPI label="ТОП-10" value={top10Now} delta={deltaPct(top10Now, top10Old)} spark={sparkTop10} color={C.info} t={t} dark={dark} />
        <KPI label="Реф. домени" value={refLatest} delta={refDelta} deltaUnit="" color={C.brand} t={t} dark={dark} />
        <KPI label="Беклінки" value={blLatest} delta={blDelta} deltaUnit="" color={C.brand} t={t} dark={dark} />
        <KPI label="Domain Rating" value={drLatest} delta={drDelta} deltaUnit="" color={C.pos} t={t} dark={dark} />
        <KPI label="У індексі · Google" value={indexedCount} delta={null} color={C.pos} t={t} dark={dark} />
        <KPI label="Не в індексі · Google" value={notIndexedCount} delta={null} color={notIndexedCount > 0 ? C.warn : t.text} t={t} dark={dark} />
        <KPI label="Всього запитів" value={totalQ} delta={deltaPct(totalQ, totalQOld)} spark={sparkTotal} color={C.brand} t={t} dark={dark} />
        <KPI label="Сторінок" value={pagesCount} delta={null} color={t.text} t={t} dark={dark} />
      </div>

      {/* ─── ІНДЕКСАЦІЯ В GOOGLE ─── */}
      <IndexCoverageWidget t={t} dark={dark} rows={idxData?.rows || null} sum={idxData?.summary || null} onRefresh={checkCoverage} refreshing={covLoading} refreshLabel={covLabel} setView={setView} />

      {/* ─── TRAFFIC + RADAR ─── */}
      <div style={{ display: "grid", gridTemplateColumns: "1.7fr 1fr", gap: 14 }}>
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
            <div>
              <div style={labelUp}>GSC ТРАФІК · 90 ДНІВ</div>
              <h3 style={{ margin: "4px 0 0", fontSize: 14, fontWeight: 600, color: t.text }}>
                Кліки <span style={{ color: C.info, marginLeft: 4 }}>{last30Clicks.toLocaleString("uk-UA")}</span>
                <span style={{ color: t.textMuted, margin: "0 8px" }}>·</span>
                Покази <span style={{ color: C.brand2, marginLeft: 4 }}>{last30Imp.toLocaleString("uk-UA")}</span>
              </h3>
            </div>
          </div>
          <div style={{ height: 220 }}>
            <Line
              data={{
                labels: dailyArr.map(d => fmtDateShort(d.date || d.keys?.[0])),
                datasets: [
                  { label: "Кліки", data: dailyArr.map(d => d.clicks || 0), borderColor: C.info, backgroundColor: "rgba(59,130,246,.12)", fill: true, tension: 0.4, pointRadius: 0, borderWidth: 2, yAxisID: "y" },
                  { label: "Покази", data: dailyArr.map(d => d.impressions || 0), borderColor: C.brand, backgroundColor: "rgba(99,102,241,.06)", fill: true, tension: 0.4, pointRadius: 0, borderWidth: 2, yAxisID: "y1" },
                ],
              }}
              options={{
                ...chartOpts(false),
                scales: {
                  x: { ticks: { color: t.textMuted, font: { size: 10 }, maxTicksLimit: 10 }, grid: { display: false }, border: { display: false } },
                  y: { position: "left", ticks: { color: C.info, font: { size: 10 } }, grid: { color: dark ? "rgba(255,255,255,.04)" : "rgba(0,0,0,.04)" }, border: { display: false } },
                  y1: { position: "right", ticks: { color: C.brand, font: { size: 10 } }, grid: { display: false }, border: { display: false } },
                },
              }}
            />
          </div>
        </div>

        <div style={{ ...card, display: "flex", flexDirection: "column" }}>
          <div style={labelUp}>SEO ЗДОРОВ'Я</div>
          <h3 style={{ margin: "4px 0 12px", fontSize: 14, fontWeight: 600, color: t.text }}>Радар по системах</h3>
          <div style={{ flex: 1, display: "grid", placeItems: "center" }}>
            <Radar
              axes={["Технічно", "Контент", "AI", "Швидкість", "Беклінки", "GSC", "Структура"]}
              values={radarValues}
              size={220}
              t={t} dark={dark}
            />
          </div>
        </div>
      </div>

      {/* ─── POSITIONS GROWTH + COCOON ─── */}
      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 14 }}>
        <div style={card}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            <div>
              <div style={labelUp}>ЗРОСТАННЯ ПОЗИЦІЙ</div>
              <h3 style={{ margin: "4px 0 0", fontSize: 14, fontWeight: 600, color: t.text }}>
                TOP-3 / TOP-10 / TOP-100
                {latest && <span style={{ fontWeight: 500, color: t.textMuted, fontSize: 12, marginLeft: 8 }}>· останнє {fmtDate(latest.date || latest.snapshot_date)}</span>}
              </h3>
            </div>
            <span style={{ fontSize: 10, color: t.textMuted }}>{snapsArr.length} знять · джерело: {snapsSource}</span>
          </div>
          <div style={{ height: 220, marginTop: 12 }}>
            <Line
              data={{
                labels: recentSnaps.map(s => fmtDateShort(s.date || s.snapshot_date)),
                datasets: [
                  { label: "Top-3", data: recentSnaps.map(s => s.top3 || 0), borderColor: C.pos, backgroundColor: "rgba(16,185,129,.16)", fill: true, tension: 0.4, pointRadius: 3, borderWidth: 2 },
                  { label: "Top-10", data: recentSnaps.map(s => s.top10 || 0), borderColor: C.brand, backgroundColor: "rgba(99,102,241,.10)", fill: true, tension: 0.4, pointRadius: 3, borderWidth: 2 },
                  { label: "Top-100", data: recentSnaps.map(s => s.top100 || 0), borderColor: C.warn, backgroundColor: "rgba(245,158,11,.06)", fill: true, tension: 0.4, pointRadius: 3, borderWidth: 2 },
                ],
              }}
              options={chartOpts(true)}
            />
          </div>
          {recentSnaps.length >= 2 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginTop: 12 }}>
              {[
                { l: "TOP-3", a: top3Old, b: top3Now, c: C.pos },
                { l: "TOP-10", a: top10Old, b: top10Now, c: C.brand },
                { l: "TOP-100", a: top100Old, b: top100Now, c: C.warn },
              ].map(m => {
                const d = m.b - m.a;
                const pct = m.a ? Math.round((d / m.a) * 100) : 0;
                return (
                  <div key={m.l} style={{ padding: 10, background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)", borderRadius: 8, textAlign: "center" }}>
                    <div style={{ fontSize: 9, color: m.c, fontWeight: 700, textTransform: "uppercase" }}>{m.l}</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: t.text, marginTop: 2 }}>{m.a} → {m.b}</div>
                    <div style={{ fontSize: 11, color: m.c, fontWeight: 600, marginTop: 2 }}>{d >= 0 ? "+" : ""}{d} ({pct >= 0 ? "+" : ""}{pct}%)</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
            <div style={labelUp}>SEO КОКОН · 3D</div>
            {data.linkGraphSummary && <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, background: "rgba(99,102,241,0.12)", color: C.brand }}>LIVE</span>}
          </div>
          <h3 style={{ margin: "4px 0 12px", fontSize: 14, fontWeight: 600, color: t.text }}>Карта перелінковки</h3>
          <div style={{ position: "relative", borderRadius: 10, overflow: "hidden", border: `1px solid ${t.border}` }}>
            <CocoonMini />
          </div>
          {data.linkGraphSummary && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginTop: 10 }}>
              {[
                { l: "Сторінок", v: data.linkGraphSummary.pages, c: C.brand2 },
                { l: "Зв'язків", v: data.linkGraphSummary.links, c: C.info },
                { l: "Сер./стор.", v: data.linkGraphSummary.avgPerPage, c: C.pos },
              ].map(m => (
                <div key={m.l}>
                  <div style={{ fontSize: 9, color: t.textMuted, fontWeight: 700, textTransform: "uppercase" }}>{m.l}</div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: m.c, marginTop: 2 }}>{typeof m.v === "number" ? m.v.toLocaleString("uk-UA") : m.v}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ─── PAGESPEED + TASKS ─── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
            <div>
              <div style={labelUp}>PAGESPEED INSIGHTS</div>
              <h3 style={{ margin: "4px 0 0", fontSize: 14, fontWeight: 600, color: t.text }}>Core Web Vitals</h3>
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              {["mobile", "desktop"].map(s => (
                <button key={s} onClick={() => setPsStrategy(s)}
                  style={{
                    background: psStrategy === s ? C.brand : "transparent", color: psStrategy === s ? "#fff" : t.textMuted,
                    border: `1px solid ${psStrategy === s ? C.brand : t.border}`, borderRadius: 6, padding: "4px 10px", fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                  }}>{s === "mobile" ? "Mobile" : "Desktop"}</button>
              ))}
            </div>
          </div>
          {ps ? (
            <>
              <div style={{ display: "flex", gap: 14, justifyContent: "center", padding: "8px 0" }}>
                <Gauge value={ps.performance} label="PERF" />
                <Gauge value={ps.accessibility} label="A11Y" />
                <Gauge value={ps.best_practices} label="BP" />
                <Gauge value={ps.seo} label="SEO" />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginTop: 14, paddingTop: 14, borderTop: `1px solid ${t.border}` }}>
                {[
                  { l: "LCP", v: ps.lcp ? `${(ps.lcp / 1000).toFixed(1)}s` : "—", th: ps.lcp <= 2500 ? C.pos : ps.lcp <= 4000 ? C.warn : C.neg },
                  { l: "CLS", v: ps.cls != null ? Number(ps.cls).toFixed(2) : "—", th: ps.cls <= 0.1 ? C.pos : ps.cls <= 0.25 ? C.warn : C.neg },
                  { l: "FCP", v: ps.fcp ? `${(ps.fcp / 1000).toFixed(1)}s` : "—", th: ps.fcp <= 1800 ? C.pos : ps.fcp <= 3000 ? C.warn : C.neg },
                  { l: "TBT", v: ps.tbt != null ? `${Math.round(ps.tbt)}ms` : "—", th: ps.tbt <= 200 ? C.pos : ps.tbt <= 600 ? C.warn : C.neg },
                ].map(m => (
                  <div key={m.l} style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 9, color: t.textMuted, fontWeight: 700, textTransform: "uppercase" }}>{m.l}</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: m.th, marginTop: 2 }}>{m.v}</div>
                  </div>
                ))}
              </div>
            </>
          ) : <div style={{ color: t.textMuted, fontSize: 12, padding: "20px 0", textAlign: "center" }}>Немає замірів. Запустіть PageSpeed для проекту.</div>}
        </div>

        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
            <div>
              <div style={labelUp}>ЗАВДАННЯ ПО ПРОЕКТУ · {allTasks.filter(tk => tk.status !== "done" && tk.status !== "completed").length}</div>
              <h3 style={{ margin: "4px 0 0", fontSize: 14, fontWeight: 600, color: t.text }}>Активні таски</h3>
            </div>
          </div>

          {/* per-column counts strip */}
          {kanbanColCounts.length > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
              {kanbanColCounts.map(c => (
                <span key={c.title} title={c.title} style={{
                  fontSize: 9, fontWeight: 700, padding: "3px 8px", borderRadius: 4,
                  background: `${c.color}1f`, color: c.color, display: "inline-flex", alignItems: "center", gap: 4,
                }}>
                  <span style={{ width: 4, height: 4, borderRadius: 2, background: c.color }} />
                  {c.title.length > 22 ? c.title.slice(0, 21) + "…" : c.title} · {c.count}
                </span>
              ))}
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {activeTasks.length === 0 && <div style={{ color: t.textMuted, fontSize: 12, padding: "20px 0", textAlign: "center" }}>Немає активних задач</div>}
            {activeTasks.map(tk => {
              const prColor = tk.priority === "high" || tk.priority === "urgent" ? C.neg : tk.priority === "medium" ? C.warn : t.textMuted;
              const updated = tk.updated_at ? new Date(tk.updated_at) : null;
              const updatedStr = updated ? updated.toLocaleDateString("uk-UA", { day: "2-digit", month: "2-digit" }) : "";
              return (
                <div key={tk.id} style={{
                  display: "flex", flexDirection: "column", gap: 4,
                  padding: "8px 10px",
                  border: `1px solid ${t.border}`,
                  borderLeft: `3px solid ${tk.columnColor || C.brand}`,
                  borderRadius: 6,
                  background: dark ? "rgba(255,255,255,0.015)" : "rgba(0,0,0,0.015)",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 3, textAlign: "center", color: tk.columnColor || C.brand, background: `${tk.columnColor || C.brand}1f` }}>
                      {tk.columnTitle}
                    </span>
                    {tk.qa_passed_at && (
                      <span title={`Пройшла QA${tk.qa_passed_by_name ? ` · ${tk.qa_passed_by_name}` : ""}`} style={{ fontSize: 8.5, fontWeight: 800, padding: "2px 6px", borderRadius: 3, background: "rgba(16,185,129,0.15)", color: C.pos }}>✓ QA</span>
                    )}
                    {tk.dev_assignee_name && (
                      <span title={`Розробник: ${tk.dev_assignee_name}`} style={{ fontSize: 8.5, fontWeight: 700, padding: "2px 6px", borderRadius: 3, background: "rgba(168,85,247,0.12)", color: C.brand2 }}>👨‍💻 {tk.dev_assignee_name.split("@")[0]}</span>
                    )}
                    {tk.priority && tk.priority !== "low" && (
                      <span style={{ fontSize: 8.5, fontWeight: 700, color: prColor, marginLeft: "auto" }}>{tk.priority.toUpperCase()}</span>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ flex: 1, fontSize: 12, color: t.text, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tk.title}</span>
                    {updatedStr && <span style={{ fontSize: 9.5, color: t.textMuted, flexShrink: 0 }}>{updatedStr}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ─── GA4 ANALYTICS ─── */}
      {data.ga4 && (
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4, flexWrap: "wrap", gap: 8 }}>
            <div>
              <div style={labelUp}>GA4 АНАЛІТИКА · {data.ga4.days || 30}Д</div>
              <h3 style={{ margin: "4px 0 0", fontSize: 14, fontWeight: 600, color: t.text }}>
                Поведінка користувачів
                {data.ga4.snapshotDate && <span style={{ fontWeight: 500, color: t.textMuted, fontSize: 11, marginLeft: 8 }}>· оновлено {fmtDate(data.ga4.snapshotDate)}</span>}
              </h3>
            </div>
            <button
              onClick={() => setView && setView("analytics")}
              style={{ background: t.bgCard, border: `1px solid ${t.border}`, color: t.text, borderRadius: 8, padding: "5px 11px", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
            >→ Аналітика</button>
          </div>

          {/* GA4 KPI strip */}
          {(() => {
            const cur = data.ga4.cur || {};
            const prev = data.ga4.prev || {};
            const pct = (c, p) => p > 0 ? Math.round(((c - p) / p) * 100) : null;
            const items = [
              { l: "Сесії", v: cur.sessions, d: pct(cur.sessions, prev.sessions), c: C.brand },
              { l: "Користувачі", v: cur.users, d: pct(cur.users, prev.users), c: C.brand2 },
              { l: "Нові", v: cur.newUsers, d: pct(cur.newUsers, prev.newUsers), c: C.info },
              { l: "Engaged", v: cur.engagedSessions, d: pct(cur.engagedSessions, prev.engagedSessions), c: C.pos },
              { l: "Перегляди", v: cur.pageViews, d: pct(cur.pageViews, prev.pageViews), c: "#0ea5e9" },
              { l: "Конверсії", v: cur.conversions, d: pct(cur.conversions, prev.conversions), c: C.warn },
            ];
            return (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginTop: 12, marginBottom: 14 }}>
                {items.map((m, i) => (
                  <div key={i} style={{ background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)", borderRadius: 10, padding: "10px 12px" }}>
                    <div style={{ fontSize: 9.5, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.1em" }}>{m.l}</div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: m.c, marginTop: 4, fontFamily: "JetBrains Mono, monospace" }}>{(m.v || 0).toLocaleString("uk-UA")}</div>
                    {m.d != null && (
                      <div style={{ fontSize: 10, fontWeight: 600, color: m.d >= 0 ? C.pos : C.neg, marginTop: 2 }}>
                        {m.d >= 0 ? "▲" : "▼"} {Math.abs(m.d)}%
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Two-column: timeseries chart + channels */}
          <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 14 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>Динаміка</div>
              {data.ga4.timeseries?.length > 0 ? (
                <div style={{ height: 180 }}>
                  <Line
                    data={{
                      labels: data.ga4.timeseries.map(d => {
                        const s = d.date; return s ? `${s.slice(6, 8)}.${s.slice(4, 6)}` : "";
                      }),
                      datasets: [
                        { label: "Сесії", data: data.ga4.timeseries.map(d => d.sessions), borderColor: C.brand, backgroundColor: "rgba(99,102,241,.12)", fill: true, tension: 0.4, pointRadius: 0, borderWidth: 2 },
                        { label: "Користувачі", data: data.ga4.timeseries.map(d => d.users), borderColor: C.brand2, backgroundColor: "rgba(168,85,247,.08)", fill: true, tension: 0.4, pointRadius: 0, borderWidth: 2 },
                      ],
                    }}
                    options={chartOpts(false)}
                  />
                </div>
              ) : (
                <div style={{ color: t.textMuted, fontSize: 12, padding: "30px 0", textAlign: "center" }}>Немає даних</div>
              )}
            </div>

            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>Канали</div>
              {data.ga4.sources?.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {data.ga4.sources.slice(0, 6).map((s, i) => {
                    const max = Math.max(...data.ga4.sources.map(x => x.sessions || 0), 1);
                    const pct = Math.round(((s.sessions || 0) / max) * 100);
                    return (
                      <div key={i} style={{ fontSize: 11.5 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                          <span style={{ color: t.text, fontWeight: 500 }}>{s.channel || "—"}</span>
                          <span style={{ color: t.textMuted, fontFamily: "JetBrains Mono, monospace" }}>{(s.sessions || 0).toLocaleString("uk-UA")}</span>
                        </div>
                        <div style={{ height: 4, background: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)", borderRadius: 2, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${pct}%`, background: C.brand, transition: "width .4s" }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ color: t.textMuted, fontSize: 12, padding: "20px 0", textAlign: "center" }}>Немає даних</div>
              )}
            </div>
          </div>
        </div>
      )}

      <PageStatusMonitor activeId={activeId} apiFetch={apiFetch} t={t} dark={dark} />

      {/* ─── HEATMAP ─── */}
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4, flexWrap: "wrap", gap: 8 }}>
          <div>
            <div style={labelUp}>ТЕПЛОВА КАРТА</div>
            <h3 style={{ margin: "4px 0 0", fontSize: 14, fontWeight: 600, color: t.text }}>
              Динаміка позицій · топ-12 ключів × останні 90 днів
              {hm?.source && <span style={{ fontWeight: 500, color: t.textMuted, fontSize: 11, marginLeft: 8 }}>· {hm.source === "serper" ? "Serper" : "GSC"}</span>}
            </h3>
          </div>
          <div style={{ fontSize: 10, color: t.textMuted, display: "flex", gap: 10, alignItems: "center" }}>
            <span><span style={{ color: C.pos }}>●</span> TOP 1-3</span>
            <span><span style={{ color: "#84cc16" }}>●</span> 4-10</span>
            <span><span style={{ color: C.warn }}>●</span> 11-30</span>
            <span><span style={{ color: "#fb923c" }}>●</span> 31-50</span>
            <span><span style={{ color: C.neg }}>●</span> &gt;50</span>
          </div>
        </div>
        <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 12 }}>
          Кожна клітинка = позиція ключа в окремий день знятія. Зелене — у топі видачі, червоне — далеко. Порожня = в той день не було знятія.
        </div>
        {hm && hm.queries?.length ? (
          <Heatmap rows={hm.queries} cols={hm.dates} data={hm.matrix} t={t} dark={dark} />
        ) : (
          <div style={{ color: t.textMuted, fontSize: 12, padding: "30px 0", textAlign: "center" }}>Немає даних. Зробіть кілька знять позицій за останні 90 днів.</div>
        )}
      </div>

      {/* ─── POSITION DISTRIBUTION ─── */}
      {latest && (
        <div style={card}>
          <div style={labelUp}>СТРУКТУРА КЛЮЧІВ</div>
          <h3 style={{ margin: "4px 0 14px", fontSize: 14, fontWeight: 600, color: t.text }}>Розподіл по позиціях (останнє зняття)</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {positionDistribution.map(r => (
              <div key={r.p} style={{ display: "flex", gap: 12, alignItems: "center", fontSize: 12 }}>
                <span style={{ width: 70, color: t.text, fontWeight: 600 }}>{r.p}</span>
                <div style={{ flex: 1, height: 22, background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)", borderRadius: 4, position: "relative", overflow: "hidden" }}>
                  <div style={{
                    position: "absolute", inset: 0, width: `${(r.v / posDistMax) * 100}%`,
                    background: `linear-gradient(90deg, ${r.c}, ${r.c}dd)`,
                    transition: "width 0.6s ease",
                  }} />
                  <span style={{ position: "absolute", right: 8, top: 3, fontSize: 11, color: t.text, fontWeight: 700 }}>{r.v}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}


function PageStatusMonitor({ activeId, apiFetch, t, dark }) {
  const [pages, setPages] = useState([]);
  const [summary, setSummary] = useState(null);
  const [feed, setFeed] = useState([]);
  const [checking, setChecking] = useState(false);
  const [monitor, setMonitor] = useState(true);
  const idxRef = useRef(0);
  const keyRef = useRef(0);

  useEffect(() => {
    if (!activeId) return;
    let alive = true;
    setPages([]); setFeed([]); idxRef.current = 0;
    const load = async (kick) => {
      try {
        const r = await apiFetch(`/api/projects/${activeId}/page-status`);
        const d = await r.json();
        if (!alive) return;
        setPages(d.pages || []); setSummary(d.summary || null); setChecking(!!d.checking); setMonitor(d.monitor !== false);
        if (d.monitor !== false && (!d.pages || !d.pages.length) && kick) {
          setChecking(true);
          await apiFetch(`/api/projects/${activeId}/page-status/check`, { method: "POST" }).catch(() => {});
          setTimeout(() => { if (alive) load(false); }, 9000);
        } else if (d.monitor !== false && d.checking) {
          setTimeout(() => { if (alive) load(false); }, 9000);
        }
      } catch (e) {}
    };
    load(true);
    return () => { alive = false; };
  }, [activeId, apiFetch]);

  useEffect(() => {
    if (!pages.length || !monitor) return;
    const iv = setInterval(() => {
      const p = pages[idxRef.current % pages.length];
      idxRef.current++;
      const key = keyRef.current++;
      setFeed(f => [{ url: p.url, status: p.status_code, key, phase: "checking" }, ...f].slice(0, 6));
      setTimeout(() => setFeed(f => f.map(it => it.key === key ? { ...it, phase: "done" } : it)), 700);
    }, 1400);
    return () => clearInterval(iv);
  }, [pages, monitor]);

  const toggleMonitor = async () => {
    const next = !monitor;
    setMonitor(next);
    try { await apiFetch(`/api/projects/${activeId}/page-status/monitor`, { method: "POST", body: JSON.stringify({ enabled: next }) }); } catch (e) {}
    if (next) { setChecking(true); apiFetch(`/api/projects/${activeId}/page-status/check`, { method: "POST" }).catch(() => {}); }
  };
  const card = { background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 12, padding: 18, marginBottom: 14 };
  const sColor = (st) => (st >= 200 && st < 300) ? "#16a34a" : (st >= 300 && st < 400) ? "#f59e0b" : (!st || st === 0) ? "#9ca3af" : "#ef4444";
  const shortUrl = (u) => { try { const x = new URL(u); return x.pathname === "/" ? x.hostname : x.pathname; } catch (e) { return u; } };
  const s = summary || {};

  return (
    <div style={card}>
      <style>{`@keyframes psSlideIn{from{opacity:0;transform:translateY(-14px)}to{opacity:1;transform:translateY(0)}}@keyframes psPulse{0%,100%{opacity:1}50%{opacity:.3}}@keyframes psScan{0%{top:-2px}100%{top:188px}}`}</style>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.6 }}>🛰 Моніторинг сторінок</span>
        {s.total ? (
          <span style={{ display: "flex", gap: 10, fontSize: 12, flexWrap: "wrap", alignItems: "center" }}>
            <b style={{ color: "#16a34a" }}>{s.ok} × 200</b>
            {s.redirect > 0 && <b style={{ color: "#f59e0b" }}>{s.redirect} × 3xx</b>}
            {s.error > 0 && <b style={{ color: "#ef4444" }}>{s.error} × помилки</b>}
            <span style={{ color: t.textMuted }}>· {s.total} сторінок</span>
          </span>
        ) : <span style={{ fontSize: 12, color: t.textMuted }}>{checking ? "Перевіряємо статуси…" : "Немає даних"}</span>}
        <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: t.textMuted, fontWeight: 700, cursor: "pointer" }} title="Автоматичний щоденний обхід усіх сторінок структури. Для дуже великих сайтів вимкніть, щоб не навантажувати сайт.">
            <input type="checkbox" checked={monitor} onChange={toggleMonitor} style={{ cursor: "pointer" }} /> Авто-обхід
          </label>
          {monitor && <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: t.textMuted, fontWeight: 700 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#16a34a", animation: "psPulse 1.4s infinite" }} />LIVE</span>}
        </span>
      </div>

      <div style={{ position: "relative", height: 188, overflow: "hidden", borderRadius: 10, background: dark ? "rgba(0,0,0,0.25)" : "rgba(0,0,0,0.02)", border: `1px solid ${t.border}` }}>
        {monitor && <div style={{ position: "absolute", left: 0, right: 0, height: 2, background: "linear-gradient(90deg, transparent, #6366f1, transparent)", animation: "psScan 3.5s linear infinite", zIndex: 2, pointerEvents: "none" }} />}
        <div style={{ display: "flex", flexDirection: "column" }}>
          {feed.map(it => (
            <div key={it.key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 12px", borderBottom: `1px solid ${t.border}`, animation: "psSlideIn .35s ease", fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
              <span style={{ fontSize: 11, color: t.textMuted, flexShrink: 0 }}>GET</span>
              <span style={{ flex: 1, fontSize: 12, color: t.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={it.url}>{shortUrl(it.url)}</span>
              {it.phase === "checking" ? (
                <span style={{ fontSize: 11, color: "#6366f1", animation: "psPulse 0.8s infinite", flexShrink: 0 }}>● перевірка…</span>
              ) : (
                <span style={{ fontSize: 11, fontWeight: 700, color: "#fff", background: sColor(it.status), padding: "2px 8px", borderRadius: 5, flexShrink: 0 }}>{it.status ? it.status : "н/д"}{(it.status >= 200 && it.status < 300) ? " OK" : ""}</span>
              )}
            </div>
          ))}
          {!feed.length && (
            <div style={{ padding: 28, textAlign: "center", color: t.textMuted, fontSize: 12 }}>{!monitor ? "⏸ Авто-обхід вимкнено — показані останні відомі статуси" : (checking ? "🔎 Перевіряємо статуси сторінок…" : "Завантаження…")}</div>
          )}
        </div>
      </div>
      {s.last_checked && <div style={{ fontSize: 10, color: t.textMuted, marginTop: 8, textAlign: "right" }}>Останнє повне сканування: {new Date(s.last_checked).toLocaleString("uk-UA")} · оновлюється щодоби</div>}
    </div>
  );
}
